import {i18n} from 'src/i18n';
import {buildQuestionsFromSubTableConfig} from 'src/modules/incident/incidentSubTable';
import ModeloTareaService from 'src/modules/modeloTarea/modeloTareaService';
import {buildIsTaskQuestionsFromConfig} from 'src/modules/tarea/isTaskConfig';
import {isId23ModeloTareaTitle} from 'src/modules/tarea/medicionesParcialId23';
import {isId24ModeloTareaTitle} from 'src/modules/tarea/medicionesParcialId24';
import {getRecordId} from 'src/modules/tarea/tareaParcialHelpers';

/**
 * Construye listaProcedimiento vacía a partir del formato (modeloFormulario)
 * asociado a un modeloTarea tipo Procedimiento.
 */
export function buildBlankProcedureListFromFormato(formato: any[] | null | undefined): any[] {
  const blankFormList: any[] = [];
  if (!Array.isArray(formato) || formato.length === 0) {
    return blankFormList;
  }

  formato.forEach((seccion) => {
    blankFormList.push({
      tipo: 'seccion',
      categoria: seccion.categoria || (seccion.isDynamic ? i18n('common.section') : ''),
      isDynamic: seccion.isDynamic,
      allowNotApplicable: Boolean(seccion.allowNotApplicable),
      listIndex: blankFormList.length,
      listLength: seccion.subseccions?.length || 0,
    });
    seccion.subseccions?.forEach((subseccion) => {
      const subsectionIndex = blankFormList.length;
      const taskQuestions = subseccion.isTask ? buildIsTaskQuestionsFromConfig(subseccion) : [];
      const subTableQuestions = !subseccion.isTask ? buildQuestionsFromSubTableConfig(subseccion) : [];
      const generatedQuestions = subseccion.isTask ? taskQuestions : subTableQuestions;
      blankFormList.push({
        tipo: 'subseccion',
        categoria: subseccion.categoria || (subseccion.isDynamic ? i18n('common.subsection') : ''),
        isDynamic: subseccion.isDynamic,
        allowNotApplicable: Boolean(subseccion.allowNotApplicable),
        isTask: subseccion.isTask,
        taskConfig: subseccion.taskConfig,
        subTableConfig: subseccion.subTableConfig,
        subTableKind: subseccion.subTableKind || '',
        listIndex: subsectionIndex,
        listLength: generatedQuestions.length > 0 ? generatedQuestions.length : subseccion.preguntas?.length || 0,
      });
      if (subseccion.isTask) {
        taskQuestions.forEach((pregunta) => {
          blankFormList.push({
            tipo: 'pregunta',
            ...pregunta,
            listIndex: blankFormList.length,
            subsectionIndex,
          });
        });
      } else if (subTableQuestions.length > 0) {
        subTableQuestions.forEach((pregunta) => {
          blankFormList.push({
            tipo: 'pregunta',
            ...pregunta,
            listIndex: blankFormList.length,
            subsectionIndex,
          });
        });
      } else {
        subseccion.preguntas?.forEach((rawPregunta) => {
          const pregunta = {...rawPregunta};
          if (pregunta.tipoRespuesta === 'inspectionCheck') {
            pregunta.inspectionCheck = 'Aprobado';
          } else {
            pregunta.inspectionCheck = undefined;
          }
          blankFormList.push({
            tipo: 'pregunta',
            ...pregunta,
            listIndex: blankFormList.length,
            subsectionIndex,
          });
        });
      }
    });
  });

  return blankFormList;
}

export function buildBlankProcedureListFromModelo(modeloTarea: any): any[] {
  if (!modeloTarea || modeloTarea.tipoVisualizacion !== 'Procedimiento') {
    return [];
  }
  const formato = modeloTarea.procedimiento?.formato;
  return buildBlankProcedureListFromFormato(formato);
}

/** Tarea sintética suficiente para exportChildTasksTableBlankPdf (columnas vía listaProcedimiento). */
export function buildSyntheticTaskFromModeloTarea(modeloTarea: any) {
  const listaProcedimiento = buildBlankProcedureListFromModelo(modeloTarea);
  const titulo = modeloTarea?.titulo || '';
  const primeroDelParcial = isId23ModeloTareaTitle(titulo) || isId24ModeloTareaTitle(titulo);
  return {
    id: `blank-template-${getRecordId(modeloTarea) || 'modelo'}`,
    titulo,
    tituloTarea: titulo,
    modeloTarea,
    listaProcedimiento,
    tipoVisualizacion: modeloTarea?.tipoVisualizacion || 'Procedimiento',
    ...(primeroDelParcial ? {primeroDelParcial: true} : {}),
  };
}

/**
 * Lista modeloTarea cuyo título empieza por alguno de los prefijos dados.
 * El backend filtra por contains; el startsWith se aplica en cliente.
 */
export async function listModeloTareasByTitlePrefixes(
  prefixes: string[],
  {limit = 50}: {limit?: number} = {},
): Promise<Array<{id: string; titulo: string}>> {
  const normalizedPrefixes = (prefixes || [])
    .map((p) => String(p || '').trim().toUpperCase())
    .filter(Boolean);
  if (!normalizedPrefixes.length) return [];

  const responses = await Promise.all(
    normalizedPrefixes.map((prefix) =>
      ModeloTareaService.list({titulo: prefix}, 'titulo_ASC', limit, 0).catch(() => ({rows: []})),
    ),
  );

  const byId = new Map<string, {id: string; titulo: string}>();
  responses.forEach((response) => {
    (response?.rows || []).forEach((row: any) => {
      const id = getRecordId(row);
      const titulo = String(row?.titulo || '').trim();
      if (!id || !titulo) return;
      const upper = titulo.toUpperCase();
      if (!normalizedPrefixes.some((prefix) => upper.startsWith(prefix))) return;
      if (!byId.has(id)) {
        byId.set(id, {id, titulo});
      }
    });
  });

  return Array.from(byId.values()).sort((a, b) => a.titulo.localeCompare(b.titulo, 'es'));
}
